import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import MobilePopup from '../../../../component/newPopup/mobilePopup/MobilePopup';
import {EventsTable, CreateEventPopup, HeaderComponent, SearchFilterComponent, FooterComponent} from './components';
import {useEventSelection} from './hooks/useEventSelection';
import * as css from './AssigningEventsPop.css';
import {AssigningEventsPopProps, StatusFilter, CategoryFilter} from './utils/types';
import useShow from '../../../../hooks/useShow';
import {getSubscriptionEvents} from '../../../../redux/subscription/subscriptionActions';
import {TState} from '../../../../redux/store.type';
import {assignEventsToPlan} from '../../../../redux/subscription/subscriptionActions';
import setGlobalState from '../../../../redux/setGlobalState';
import {getEventStatus} from './utils/utils';

function AssigningEventsPop({planTid, show, onClose, subscriptionTitle, subscriptionSubtitle, showEventKind = false}: AssigningEventsPopProps) {
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
    const [searchValue, setSearchValue] = useState<string>('');
    const [lastOffset, setLastOffset] = useState<number>(-1);
    const createEventShow = useShow();
    
    const subscriptionEvents = useSelector((s: TState) => s.subscription.events);
    const getSubscriptionEventsXHR = useSelector((s: TState) => s.subscription.getSubscriptionEventsXHR);
    const plan = useSelector((s: TState) => s.subscription.subscriptionPlans?.find(p => p.planTid === planTid));

    // Get active events for selection logic
    const activeEvents = useMemo(() => {
        if(!subscriptionEvents?.length) return [];
        return subscriptionEvents.filter(event => {
            const status = getEventStatus(event);
            return status === 'active';
        });
    }, [subscriptionEvents]);

    const {selectedEvents, mandatoryCount, optionalCount, allActiveSelected, handleEventSelect, handleEventKindChange, handleSelectAllActive} = useEventSelection({
        activeEvents,
        saleStart: plan?.saleStart,
        saleEnd: plan?.saleEnd,
    });

    useEffect(() => {
        setLastOffset(-1);
        setGlobalState('subscription.events', []);
    }, [searchValue, statusFilter, categoryFilter, planTid]);

    const loadEvents = useCallback((offset: number) => {
        setLastOffset(offset);
        getSubscriptionEvents({
            planTid,
            limit: 10,
            offset,
            search: searchValue || null,
            statusFilter,
            categoryFilter
        }).then(res => {
            res?.events?.forEach((event: any) => {
                handleEventSelect(event.eid, event.isInPlan);
            });
        });
    }, [planTid, searchValue, statusFilter, categoryFilter, handleEventSelect]);

    useEffect(() => {
        if(getSubscriptionEventsXHR === 'LOADING') return;

        const currentOffset = subscriptionEvents?.length ?? 0;
        const shouldLoadInitial = lastOffset === -1;
        const shouldLoadMore = lastOffset !== currentOffset;

        if(shouldLoadInitial) {
            loadEvents(0);
        } else if(shouldLoadMore) {
            loadEvents(currentOffset);
        }
    }, [subscriptionEvents?.length, getSubscriptionEventsXHR, lastOffset, loadEvents]);

    useEffect(() => {
        return () => {
            setGlobalState('subscription.events', []);
            setLastOffset(-1);
        };
    }, []);
    const handleSave = useCallback(() => {
        assignEventsToPlan(planTid, selectedEvents.map(event => event.eid)).then(() => {});
        onClose();
    }, [selectedEvents]);

    return (
        <>
            <css.assigningEventsPop>
                <MobilePopup 
                    showClass={show.showClass}
                    close={onClose}
                    title=""
                    dataCyName="assigningEvents"
                    list={undefined} 
                    forwardRef={undefined}
                    height={'90vh'}
                    TopComponent={null}
                >
                    <>
                        <css.container>
                            <HeaderComponent
                                planEventsCount={selectedEvents.length}
                                mandatoryCount={mandatoryCount}
                                optionalCount={optionalCount}
                                onCreateEvent={createEventShow.open}
                                subscriptionTitle={subscriptionTitle}
                                subscriptionSubtitle={subscriptionSubtitle}
                                onClose={onClose}
                                showEventKind={showEventKind}
                            />
                            <SearchFilterComponent
                                statusFilter={statusFilter}
                                categoryFilter={categoryFilter}
                                events={subscriptionEvents}
                                onStatusFilterChange={setStatusFilter}
                                onCategoryFilterChange={setCategoryFilter}
                                onSearchChange={setSearchValue}
                            />
                            <EventsTable 
                                events={subscriptionEvents}
                                selectedEvents={selectedEvents}
                                onEventSelect={handleEventSelect}
                                onEventKindChange={handleEventKindChange}
                                loading={getSubscriptionEventsXHR}
                                showEventKind={showEventKind}
                                saleStart={plan?.saleStart}
                                saleEnd={plan?.saleEnd}
                            />
                        </css.container>
                        <FooterComponent
                            allActiveSelected={allActiveSelected}
                            onSelectAllActive={handleSelectAllActive}
                            onClose={onClose}
                            onSave={handleSave}
                            saveDisabled={getSubscriptionEventsXHR === 'LOADING'}
                            showSelectAll={activeEvents.length > 0}
                        />
                    </>
                </MobilePopup> 
            </css.assigningEventsPop>
            {createEventShow.isOpen && (
                <CreateEventPopup 
                    show={createEventShow}
                />
            )}
        </>
    );
}

export default AssigningEventsPop;
